import type { ImportProblem, ImportRow, ImportRowEdit, SaleInput } from '@oneapp/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { businessApi, businessKeys } from '../business/api';
import { Sheet } from '../components/Sheet';
import { Button, Callout } from '../components/ui';
import { ApiError } from '../lib/api';
import { salesApi, salesKeys } from '../sales/api';
import { SaleForm, type SaleFormStart, type SaveProblems } from '../sales/SaleForm';
import { importKeys, importsApi, parseImportPath } from './api';
import { PhotoView } from './PhotoView';

/**
 * Fix a row of an imported file: the Edit sale form's layout, filled with what the file said, with
 * what's wrong above it. Saving checks the row again: it closes when nothing's left, or says what still
 * is. Opened from the chat's "Fix row 6" (a sheet over it) or on its own from a link.
 */

export type RowFinish = 'saved' | 'back';

interface Screen {
  label: string;
  body: ReactNode;
  footer: ReactNode;
}

/** The row's values as the form starts from them. */
function startOf(row: ImportRow): SaleFormStart {
  const f = row.fields;
  return {
    buyerGstin: f.buyerGstin,
    number: f.invoiceNumber ?? '',
    date: f.invoiceDate ?? '',
    hsn: f.hsn && /^\d{4}(\d{2}){0,2}$/.test(f.hsn) ? f.hsn : '',
    description: f.description ?? '',
    rate: f.rate,
    quantity: f.quantity,
    taxableValue: f.taxableValue !== null && f.taxableValue > 0 ? f.taxableValue : null,
    placeOfSupply: f.placeOfSupply,
  };
}

/** The form's sale as changes to the row. A registered buyer's place of supply stays as the file had it. */
function editOf(input: SaleInput): ImportRowEdit {
  const line = input.lines[0];
  return {
    invoiceNumber: input.numbers[0] ?? '',
    invoiceDate: input.date,
    buyerGstin: input.buyerGstin,
    ...(input.buyerGstin ? {} : { placeOfSupply: input.placeOfSupply ?? null }),
    ...(line
      ? {
          hsn: line.hsn,
          description: line.description ?? null,
          quantity: line.quantity ?? null,
          rate: line.rate,
          taxableValue: line.taxableValue,
        }
      : {}),
  };
}

/** A row's problems as the form shows them: by field where it has one, the rest above it. */
function leftOf(problems: ImportProblem[]): SaveProblems | null {
  if (!problems.length) return null;
  const fields: Record<string, string> = {};
  const rest: string[] = [];
  for (const p of problems) {
    if (p.field === 'row' || p.field === 'placeOfSupply' || p.field === 'description') rest.push(p.message);
    else fields[p.field] = fields[p.field] ?? p.message;
  }
  return { fields, message: rest.join(' ') || null };
}

function useFixRowScreen(importId: string, rowId: string, problemId: string | null, finish: (how: RowFinish) => void) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const formId = useId();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const rowQuery = useQuery({
    queryKey: importKeys.row(importId, rowId),
    queryFn: ({ signal }) => importsApi.row(importId, rowId, signal),
    staleTime: 0,
  });
  const returnId = rowQuery.data?.import.returnId ?? '';
  const draftQuery = useQuery({
    queryKey: salesKeys.draft(returnId),
    queryFn: ({ signal }) => salesApi.draft(returnId, signal),
    enabled: Boolean(returnId),
  });
  const businesses = useQuery({ queryKey: businessKeys.all, queryFn: ({ signal }) => businessApi.list(signal) });

  const data = rowQuery.data;
  const draft = draftQuery.data?.return;
  const seller = businesses.data?.businesses.find((b) => b.id === draft?.businessId);
  const screen: Screen = { label: data?.import.fileName ?? t('sales.loading'), body: null, footer: null };

  if (rowQuery.isPending || (returnId && draftQuery.isPending) || businesses.isPending) {
    screen.body = <p className="muted">{t('sales.loading')}</p>;
    return screen;
  }
  if (!data || !draft || !seller) {
    const notFound = rowQuery.error instanceof ApiError && rowQuery.error.status === 404;
    screen.body = (
      <Callout tone="error" icon="alert-triangle">
        {notFound ? t('imports.fix.notFound') : t('sales.loadFailed')}
      </Callout>
    );
    return screen;
  }

  const { row } = data;
  const open = row.problems;
  // The problem the page was opened for, else the row's first: editing settles it (and checks the rest).
  const editable =
    open.find((p) => p.id === problemId && p.actions.includes('edit')) ?? open.find((p) => p.actions.includes('edit'));
  const skippable = open.find((p) => p.actions.includes('skip'));
  // Photos (Phase 8): a row is an invoice read off a photo, and the photo shows beside it.
  const photos = data.import.kind === 'invoice_photos';
  const where = photos
    ? t('imports.fix.whereInvoice', { row: row.row })
    : row.sheet
      ? t('imports.fix.whereSheet', { sheet: row.sheet, row: row.row })
      : t('imports.fix.where', { row: row.row });

  const refresh = async () => {
    const fresh = await importsApi.row(importId, rowId);
    queryClient.setQueryData(importKeys.row(importId, rowId), fresh);
    void queryClient.invalidateQueries({ queryKey: importKeys.import(importId) });
    return fresh;
  };

  const save = async (input: SaleInput): Promise<SaveProblems | null> => {
    if (!editable) return { fields: {}, message: t('imports.fix.cantEdit') };
    await importsApi.resolve(importId, editable.id, { action: 'edit', values: editOf(input) });
    const fresh = await refresh();
    const left = leftOf(fresh.row.problems);
    if (!left) finish('saved');
    return left;
  };

  const skip = async () => {
    if (!skippable) return;
    setBusy(true);
    setFailed(false);
    try {
      await importsApi.resolve(importId, skippable.id, { action: 'skip' });
      finish('saved');
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  screen.label = data.import.fileName;
  screen.body = (
    <SaleForm
      key={`${row.id}-${data.import.version}`}
      returnId={draft.id}
      draft={draft}
      sellerGstin={seller.gstin}
      start={startOf(row)}
      heading={photos ? t('imports.fix.titleInvoice', { row: row.row }) : t('imports.fix.title', { row: row.row })}
      lead={
        <>
          {open.length ? (
            <Callout tone="amber" icon="alert-triangle">
              <strong>{row.fields.invoiceNumber ? `${where} · ${row.fields.invoiceNumber}` : where}</strong>
              {open.map((p) => (
                <span key={p.id} className="fix-row__problem">
                  {p.message}
                </span>
              ))}
            </Callout>
          ) : (
            <Callout tone="green" icon="check-circle">
              {t('imports.fix.nothingLeft')}
            </Callout>
          )}
          {failed ? (
            <Callout tone="error" icon="alert-triangle" role="alert">
              {t('sales.form.failed')}
            </Callout>
          ) : null}
          {photos && row.uploadId ? <PhotoView uploadId={row.uploadId} number={row.row} area={null} /> : null}
          <details className="fix-row__file">
            <summary>{photos ? t('imports.fix.onPhoto') : t('imports.fix.inFile')}</summary>
            <dl className="fields">
              {Object.entries(row.cells).map(([header, value]) => (
                <div key={header} className="fields__row">
                  <dt>{header}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>
          </details>
        </>
      }
      formId={formId}
      save={save}
      onBusy={setBusy}
    />
  );
  screen.footer = (
    <div className="sheet__buttons">
      {skippable ? (
        <Button variant="secondary" onClick={skip} disabled={busy}>
          {photos ? t('imports.fix.skipInvoice', { row: row.row }) : t('imports.fix.skip', { row: row.row })}
        </Button>
      ) : null}
      <Button type="submit" form={formId} busy={busy} disabled={!editable}>
        {t('imports.fix.save')}
      </Button>
    </div>
  );
  return screen;
}

/** Opened from the chat's "Fix row N": the sheet over it. */
export function FixRowSheet({
  path,
  onClose,
  onFinish,
}: {
  path: string;
  onClose: () => void;
  onFinish: (how: RowFinish) => void;
}) {
  const parsed = parseImportPath(path);
  const screen = useFixRowScreen(parsed?.importId ?? '', parsed?.rowId ?? '', parsed?.problemId ?? null, onFinish);
  return (
    <Sheet open onClose={onClose} label={screen.label} footer={screen.footer}>
      {screen.body}
    </Sheet>
  );
}

/** The same page on its own, for a link to it. */
export function FixRowStandalone({
  importId,
  rowId,
  problemId,
  onFinish,
  frame,
}: {
  importId: string;
  rowId: string;
  problemId: string | null;
  onFinish: (how: RowFinish) => void;
  frame: (screen: Screen) => ReactNode;
}) {
  const screen = useFixRowScreen(importId, rowId, problemId, onFinish);
  return <>{frame(screen)}</>;
}
